import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { Button } from '@/components/ui/Button';

const { FiExternalLink, FiZap, FiArrowRight, FiShield, FiCpu, FiGlobe } = FiIcons;

const Splash = () => {
  const navigate = useNavigate();
  const newUrl = "https://admin.cisplupdation.in/";

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -right-1/4 w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] bg-accent-600/10 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #6366f1 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full relative z-10"
      >
        <div className="text-center space-y-8">
          {/* Logo & Badge */}
          <div className="flex flex-col items-center space-y-4">
            <motion.div 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 rounded-[2rem] bg-gradient-to-br from-primary-500 to-indigo-600 flex items-center justify-center shadow-2xl shadow-primary-500/40"
            >
              <SafeIcon icon={FiZap} className="w-10 h-10 text-white" />
            </motion.div>
            <div className="inline-flex items-center space-x-2 bg-primary-500/10 border border-primary-500/20 rounded-full px-4 py-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
              </span>
              <span className="text-primary-400 text-[10px] font-black uppercase tracking-[0.2em]">System Migration in Progress</span>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-4">
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-tight">
              We've Moved to a <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-400">New Experience</span>
            </h1>
            <p className="text-surface-400 text-lg md:text-xl max-w-lg mx-auto font-medium leading-relaxed">
              The Rewards Admin ecosystem has been upgraded. Please use the new dedicated portal for enhanced performance and features.
            </p>
          </div>

          {/* Action Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
            <motion.a
              href={newUrl}
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="group bg-primary-600 hover:bg-primary-500 p-8 rounded-[2.5rem] text-left transition-all duration-300 shadow-xl shadow-primary-900/20 flex flex-col justify-between h-64 border border-primary-400/20"
            >
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
                <SafeIcon icon={FiGlobe} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white text-xl font-black mb-2 flex items-center">
                  New Admin Panel
                  <SafeIcon icon={FiExternalLink} className="ml-2 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-primary-100 text-sm font-medium mb-4">Access the latest version with v2.0 improvements.</p>
                <div className="text-white font-black text-[10px] uppercase tracking-widest bg-black/20 py-2 px-4 rounded-xl inline-block">
                  admin.cisplupdation.in
                </div>
              </div>
            </motion.a>

            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              onClick={() => navigate('/dashboard')}
              className="group bg-surface-900 hover:bg-surface-800 p-8 rounded-[2.5rem] text-left transition-all duration-300 border border-surface-800 flex flex-col justify-between h-64 cursor-pointer"
            >
              <div className="w-12 h-12 rounded-2xl bg-surface-800 flex items-center justify-center mb-4">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-surface-400" />
              </div>
              <div>
                <h3 className="text-white text-xl font-black mb-2">Legacy Console</h3>
                <p className="text-surface-500 text-sm font-medium mb-4">Continue to the old dashboard for temporary access.</p>
                <div className="text-surface-400 font-black text-[10px] uppercase tracking-widest flex items-center group-hover:text-primary-400 transition-colors">
                  Enter Legacy System <SafeIcon icon={FiArrowRight} className="ml-2 w-3 h-3" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Footer Info */}
          <div className="pt-8 flex flex-col items-center space-y-6">
            <div className="flex items-center space-x-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCpu} className="w-4 h-4 text-white" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Enhanced Security</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiZap} className="w-4 h-4 text-white" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Fast Sync</span>
              </div>
            </div>
            <p className="text-surface-600 text-[10px] font-bold uppercase tracking-[0.3em]">
              © 2026 CISPL Gifts Portal • System v1.5.0
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Splash;